import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { workflowService } from '../../services/api/workflowService';
import {
  WorkflowConfig,
  WorkflowProductType,
  SpotTransitionApprovals,
  ForwardTransitionApprovals,
  DEFAULT_WORKFLOW_CONFIG,
} from '../../types/workflow';
import WorkflowDiagram from './WorkflowDiagram';
import WorkflowExceptionsAdminPanel from './WorkflowExceptionsAdminPanel';
import TransitionBlockAdminPanel from './TransitionBlockAdminPanel';
import './WorkflowConfigPanel.css';

const WorkflowConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<WorkflowConfig | null>(null);
  const [originalConfig, setOriginalConfig] = useState<WorkflowConfig | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [exceptionsRefreshKey, setExceptionsRefreshKey] = useState(0);
  const [blockRulesRefreshKey, setBlockRulesRefreshKey] = useState(0);

  // Load configuration on mount
  useEffect(() => {
    if (clientId) {
      loadConfig();
    }
  }, [clientId]);

  // Track changes
  useEffect(() => {
    if (config && originalConfig) {
      const hasChanged = JSON.stringify(config.approvalRequirements) !==
                         JSON.stringify(originalConfig.approvalRequirements);
      setHasChanges(hasChanged);
    }
  }, [config, originalConfig]);

  const loadConfig = async () => {
    if (!clientId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await workflowService.getWorkflowConfig(clientId);
      if (response.success && response.data) {
        setConfig(response.data);
        setOriginalConfig(JSON.parse(JSON.stringify(response.data)));
      } else {
        // Use defaults if no config exists
        setConfig(DEFAULT_WORKFLOW_CONFIG);
        setOriginalConfig(JSON.parse(JSON.stringify(DEFAULT_WORKFLOW_CONFIG)));
      }
    } catch (err) {
      // On error, use defaults
      setConfig(DEFAULT_WORKFLOW_CONFIG);
      setOriginalConfig(JSON.parse(JSON.stringify(DEFAULT_WORKFLOW_CONFIG)));
      console.error('Error loading workflow config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!clientId || !config) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await workflowService.updateWorkflowConfig(clientId, {
        approvalRequirements: config.approvalRequirements,
      });

      if (response.success) {
        setOriginalConfig(JSON.parse(JSON.stringify(config)));
        setHasChanges(false);
        setSuccess(t('settings.workflow.saveSuccess'));
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || t('settings.workflow.saveError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.workflow.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalConfig) {
      setConfig(JSON.parse(JSON.stringify(originalConfig)));
    }
  };

  const handleApprovalChange = (productType: WorkflowProductType, transitionKey: string, value: number) => {
    if (!config) return;

    const updatedConfig = { ...config };

    if (productType === 'spot') {
      updatedConfig.approvalRequirements.spot = {
        ...updatedConfig.approvalRequirements.spot,
        [transitionKey]: value,
      } as SpotTransitionApprovals;
    } else {
      updatedConfig.approvalRequirements.forward = {
        ...updatedConfig.approvalRequirements.forward,
        [transitionKey]: value,
      } as ForwardTransitionApprovals;
    }

    setConfig(updatedConfig);
  };

  if (loading) {
    return (
      <div className="workflow-config-panel loading">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="workflow-config-panel">
      {/* Header */}
      <div className="panel-header">
        <h3>{t('settings.workflow.title')}</h3>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="message-banner error">
          <p>{error}</p>
        </div>
      )}
      {success && (
        <div className="message-banner success">
          <p>{success}</p>
        </div>
      )}

      {/* Side-by-side Workflow Diagrams */}
      <div className="diagrams-container">
        {/* Spot Trades Workflow */}
        <div className="diagram-section">
          <h4 className="diagram-section-title">{t('settings.workflow.spotTrades')}</h4>
          <div className="diagram-container">
            {config && (
              <WorkflowDiagram
                productType="spot"
                approvals={config.approvalRequirements.spot}
                onApprovalChange={(transitionKey, value) => handleApprovalChange('spot', transitionKey, value)}
                disabled={saving}
              />
            )}
          </div>
        </div>

        {/* Forward Trades Workflow */}
        <div className="diagram-section">
          <h4 className="diagram-section-title">{t('settings.workflow.forwardTrades')}</h4>
          <div className="diagram-container">
            {config && (
              <WorkflowDiagram
                productType="forward"
                approvals={config.approvalRequirements.forward}
                onApprovalChange={(transitionKey, value) => handleApprovalChange('forward', transitionKey, value)}
                disabled={saving}
              />
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="panel-actions">
        <button
          className="reset-button secondary"
          onClick={handleReset}
          disabled={!hasChanges || saving}
        >
          {t('common.reset')}
        </button>
        <button
          className="save-button primary"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>

      {/* Workflow Exceptions — KAN-112 */}
      <div className="exceptions-container">
        <div className="exception-section">
          <WorkflowExceptionsAdminPanel
            productTypeHint="spot"
            externalRefreshKey={exceptionsRefreshKey}
            onRulesChanged={() => setExceptionsRefreshKey(k => k + 1)}
          />
        </div>
        <div className="exception-section">
          <WorkflowExceptionsAdminPanel
            productTypeHint="forward"
            externalRefreshKey={exceptionsRefreshKey}
            onRulesChanged={() => setExceptionsRefreshKey(k => k + 1)}
          />
        </div>
      </div>

      {/* Transition Block Rules — KAN-199 */}
      <div className="exceptions-container">
        <div className="exception-section">
          <TransitionBlockAdminPanel
            productTypeHint="spot"
            externalRefreshKey={blockRulesRefreshKey}
            onRulesChanged={() => setBlockRulesRefreshKey(k => k + 1)}
          />
        </div>
        <div className="exception-section">
          <TransitionBlockAdminPanel
            productTypeHint="forward"
            externalRefreshKey={blockRulesRefreshKey}
            onRulesChanged={() => setBlockRulesRefreshKey(k => k + 1)}
          />
        </div>
      </div>

    </div>
  );
};

export default WorkflowConfigPanel;
